import {z} from 'zod'
import moment from 'moment'
import {AddressModel} from './address.js'
import generateUUID from '../../utils/generateUUID.js'
import dbCodes from '../dbCodes.js'
import defaultWelcomeCustomerBody from './resellerDefaults/defaultWelcomeCustomerBody.js'
import defaultWelcomeUserBody from './resellerDefaults/defaultWelcomeUserBody.js'
import defaultTrackTraceBody from './resellerDefaults/defaultTrackTraceBody.js'
import defaultCancelEmailBody from './resellerDefaults/defaultCancelEmailBody.js'
import defaultEmailNotifications from './resellerDefaults/defaultEmailNotifications.js'
import guid from '../../utils/guid.js'
import {CONDITIONS_VERSION} from '../utils/migrateConditions.js'
import {IncomingPartnerConditionGroupsModel, OrderConditionGroupsModel, OutgoingPartnerConditionGroupsModel} from './order.js'
import {ParcelConditionGroupsModel} from './parcel.js'
import invalidPostalCodeRange from '../../utils/invalidPostalCodeRange.js'
import Languages from '../../utils/Languages.js'

export const OrderPickupOptions = z.enum(['gpsRequired', 'scanRequired', 'imageRequired'])

export const OrderDeliveryOptions = z.enum(['gpsRequired', 'scanRequired', 'imageRequired', 'signatureRequired', 'signatureNeighbors', 'nameRequired', 'statedAddressOnly', 'minimumAge +16', 'minimumAge +18', 'minimumAge +21', 'idCheck', 'mailboxDelivery', 'refrigerated'])

const RouteProfileModel = z.enum(['car', 'bike', 'walk'])

const FuelTypeModel = z.enum(['green-electric', 'grey-electric', 'unknown-electric', 'bio-diesel', 'diesel', 'lpg', 'hydrogen', 'none', ''])

const PayloadTypeModel = z.enum(['colli', 'weight', ''])

const ResellerAddressModel = z.object({
    name: z.string().default(''),
    attention: z.string().default(''),
    street: z.string().default(''),
    nr: z.string().default(''),
    addition: z.string().default(''),
    street2: z.string().default(''),
    postalCode: z.string().default(''),
    city: z.string().default(''),
    country: z.string().default(''),
    email: z.string().default(''),
    phone: z.string().default(''),
    state: z.string().optional(),
    isBusiness: z.boolean().optional(),
    instructions: z.string().optional(),
    position: z.object({
        lat: z.number(),
        lng: z.number()
    }).optional(),
    overridePosition: z.boolean().optional(),
    showWarning: z.boolean().optional(),
    attachments: z.object({
        fileId: z.string().optional(),
        type: z.string(),
        fileName: z.string(),
        data: z.string().optional()
    }).array().optional()
})

const PartnerStatusModel = z.enum(['inactive', 'requested', 'pending', 'active'])

const OutgoingPartnerModel = z.object({
    _id: z.string(),
    key: z.string().optional(),
    status: PartnerStatusModel,
    useCustomScanColor: z.boolean().default(false),
    customScanColor: z.string().default(''),
    creditorCode: z.string().optional(),
    communication: z.string().optional(),
    allowEditOrder: z.string().optional(),
    useCustomDeliveryZones: z.boolean().default(false),
    // Ids of the reseller's own delivery zones the partner is restricted to
    deliveryZones: z.string().array().default([]),
    // Postal codes the partner is restricted to, next to the selected delivery zones
    postalCodes: z.string().optional(),
    autoForwardOrders: z.enum(['default', 'yes', 'no']).default('default'),
    // 'default': the default conditions of forwarding orders apply, 'custom': only the conditions of this connection
    customConditions: z.enum(['default', 'custom']).default('default'),
    conditionGroups: OutgoingPartnerConditionGroupsModel,
    conditionsMatchAll: z.boolean().default(true)
})

const IncomingPartnerModel = z.object({
    _id: z.string(),
    key: z.string().optional(),
    status: PartnerStatusModel,
    showRouteInfo: z.enum(['default', 'yes', 'no']).default('default'),
    customDeliveryZones: z.string().optional(),
    deliveryZones: z.string().array().default([]),
    postalCodes: z.string().optional(),
    maxWeight: z.number().optional(),
    maxVolume: z.number().optional(),
    maxLength: z.number().optional(),
    maxWidth: z.number().optional(),
    maxHeight: z.number().optional(),
    // 'default': the default conditions of receiving orders apply, 'custom': only the conditions of this connection
    customConditions: z.enum(['default', 'custom']).default('default'),
    conditionGroups: IncomingPartnerConditionGroupsModel,
    conditionsMatchAll: z.boolean().default(true)
})

const DeliveryZoneModel = z.object({
    id: z.string(),
    name: z.string(),
    color: z.string(),
    // A zone is either drawn on the map (geoJSON) or defined by postal codes, zones saved without a type are drawn
    type: z.enum(['map', 'postalCodes']).default('map'),
    postalCodes: z.string().optional(),
    geoJSON: z.object({
        type: z.string(),
        properties: z.record(z.string(), z.any()).optional(), // Leaflet feature properties, e.g. the style of an imported KML zone
        geometry: z.object({
            type: z.string(),
            coordinates: z.array(z.array(z.array(z.number()))).optional().or(z.array(z.array(z.array(z.array(z.number())))).optional()),
            geometries: z.array(z.object({
                type: z.string(),
                coordinates: z.array(z.array(z.array(z.number()))).optional().or(z.array(z.array(z.array(z.array(z.number())))).optional())
            })).optional()
        })
    }).optional(),
    visible: z.boolean()
}).superRefine((deliveryZone, ctx) => {
    const invalidPart = deliveryZone.type === 'postalCodes' ? invalidPostalCodeRange(deliveryZone.postalCodes || '') : null

    if (invalidPart) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['postalCodes'],
            message: Languages.invalidPostalCodeRange.NL(invalidPart)
        })
    }
})

const VehicleTypeModel = z.object({
    _id: z.string().optional(),
    name: z.string().max(30, 'Naam mag maximaal 30 karakters bevatten'),
    icon: z.string(),
    routeProfile: RouteProfileModel,
    speedDifferenceEnabled: z.boolean().optional(),
    speedDifferenceMode: z.enum(['faster', 'slower']).optional(),
    speedDifference: z.number().optional(),
    fuelType: FuelTypeModel,
    /** Range of vehicle in meters. */
    range: z.number(),
    usage: z.number().optional(),
    payloads: z.array(z.object({
        payload: z.number(),
        payloadType: PayloadTypeModel,
        payloadColliType: z.string().optional()
    })),
    hourlyRate: z.string(),
    /** Stop time in seconds. */
    stopTime: z.number(),
    /** Comment for messenger. */
    comment: z.string(),
    notes: z.string(),
    isArchived: z.boolean()
})
const ShippingRuleModel = z.object({
    id: z.string(),
    name: z.string().max(50, 'Naam mag maximaal 50 karakters bevatten'),
    active: z.boolean(),
    matchAllConditions: z.boolean(),
    conditionGroups: ParcelConditionGroupsModel,
    carrier: z.string()
})

const ActivityModel = z.object({
    id: z.string(),
    name: z.string(),
    color: z.string(),
    trigger: z.enum(['', 'loading', 'route', 'other']).default(''),
    automaticOnly: z.boolean().default(false),
    lockAppActivity: z.boolean().default(false)

})

const ShippingRuleSetModel = z.object({
    id: z.string(),
    name: z.string(),
    shippingRules: z.array(ShippingRuleModel)
})

export const openingHoursDayObject = z.object({
    open: z.boolean(),
    from: z.string(),
    till: z.string()
})

const ParcelPriceTableProductModel = z.object({
    usePriceScales: z.boolean().optional(),
    countryGroups: z.array(z.object({
        name: z.string().optional(),
        countries: z.string().array(),
        type: z.enum(['', 'product', 'weight', 'fixed', 'fixedKg', 'fee', 'feepercent']),
        price: z.string().optional(),
        productCode: z.string().optional(),
        fee: z.string().optional(),
        round: z.string().optional(),
        categories: z.array(z.object({
            name: z.string().optional(),
            price: z.string().optional(),
            maxWeight: z.string().optional(),
            length: z.number().optional(),
            width: z.number().optional(),
            height: z.number().optional(),
            volume: z.number().optional(),
            productCode: z.string().optional(),
            fee: z.string().optional(),
            priceScales: z.array(z.object({
                maxAmount: z.number().optional(),
                price: z.string()
            })).default([])
        })).optional(),
        values: z.record(z.number().or(z.string()), z.string()).optional(),
        priceScales: z.array(z.object({
            maxAmount: z.number().optional(),
            price: z.string()
        })).default([])
    })).optional()
})

const ParcelFeeModel = z.object({
    id: z.string().default(() => generateUUID()), // fees added on the Productcodes page used to have no id
    description: z.string(),
    basedOn: z.enum(['fixedPrice', 'percentageOfProduct', 'percentageOfProductPlusFixedPriceFees']).default('fixedPrice'),
    price: z.string(),
    productCode: z.string(),
    auto: z.boolean().optional(),
    applyToAllCarriers: z.boolean().optional(),
    carriers: z.array(z.string()).optional(),
    applyToAllCountries: z.boolean().optional(),
    countries: z.array(z.string()).optional(),
    postalCodes: z.record(z.string(), z.string()).optional(), // countryCode -> postal code pattern, missing/empty means the whole country
    conditionGroups: ParcelConditionGroupsModel,
    matchAllConditions: z.boolean().optional(),
    quantity: z.number().optional()
})

// TODO for correct typing, move countrygroups/pricerules to a separate object
// const ParcelPriceTableModel = z.record(
//     z.string(), // carrierName
//     z.object({
//         usePriceScales: z.boolean().optional(),
//         countryGroups: z.array(ParcelPriceTableProductModel)
//     })
// )

const ParcelPriceTableModel = z.record(
    z.string(), // carrierName
    z.object({
        usePriceScales: z.boolean().optional()
    }).catchall(ParcelPriceTableProductModel)
)

const OrderFeeModel = z.object({
    id: z.string(),
    description: z.string(),
    price: z.string(),
    auto: z.boolean().optional(),
    conditionGroups: OrderConditionGroupsModel,
    matchAllConditions: z.boolean().optional()
})

const OrderPriceTableLocationModel = z.object({
    type: z.enum(['order', 'start', 'companyAddress', 'branch', 'custom']).default('order'), // 'order' = first (start) or last (end) address of the order, 'start' = back to the start location (end only)
    branchId: z.string().optional(),
    address: ResellerAddressModel.optional()
})

const OrderPriceTableModel = z.object({
    id: z.string(),
    name: z.string(),
    distanceFeeType: z.enum(['', 'fixed', 'distance', 'distanceZones', 'zones']).default('fixed'), // TODO migrate '' to 'fixed' in frontend and remove '' from enum
    useProducts: z.boolean().optional(),
    useColliType: z.boolean().optional(),
    usePriceScales: z.boolean().optional(),
    useProgressivePriceScales: z.boolean().optional(),
    usePriceScalesPerDay: z.boolean().optional(),
    useDeliveryAreas: z.boolean().optional(), // Replaced by zoneDetermination, still read when zoneDetermination is missing
    zoneDetermination: z.enum(['straightLine', 'carDistance', 'bikeDistance', 'deliveryAreas']).optional(),

    // Vaste prijs
    price: z.string().default('0,00'),
    priceScales: z.array(z.object({
        maxAmount: z.number(),
        price: z.string().default('0,00')
    })).default([]),
    products: z.record(z.string(), z.object({
        price: z.string().default('0,00'),
        priceScales: z.array(z.object({
            maxAmount: z.number().default(0),
            price: z.string().default('0,00')
        })).default([])
    })).default({}),
    colliTypePrices: z.record(z.string(), z.object({
        price: z.string().default('0,00'),
        priceScales: z.array(z.object({
            maxAmount: z.number().default(0),
            price: z.string().default('0,00')
        })).default([])
    })).default({}),
    // Separate from nrOfColliIncluded (Toeslagen), which existing tables often have at 1
    colliTypeNrOfColliIncluded: z.number().default(0),

    // Afstand
    distanceVehicleType: z.enum(['car', 'bike']).optional(), // Missing means car, the only option before it existed
    startLocation: OrderPriceTableLocationModel.default({type: 'order'}),
    endLocation: OrderPriceTableLocationModel.default({type: 'order'}),
    startDistance: z.number().optional(),
    startRate: z.string().default('0,00'),
    distanceFee: z.string().optional(),
    detourFactorPercentage: z.string().optional(),
    fuelFeeType: z.string().optional(),
    fuelFeePercent: z.string().optional(),
    rounding: z.number().or(z.literal('')).optional().nullable(),
    minimumRate: z.string(),

    // Zones
    distanceZones: z.array(z.object({
        name: z.string().default(''),
        maxDistance: z.number().default(0),
        deliveryZones: z.string().array().default([]),
        price: z.string().default('0,00'),
        nrOfColliIncluded: z.number().default(0),
        priceScales: z.array(z.object({
            maxAmount: z.number().default(0),
            price: z.string().default('0,00')
        })).default([]),
        products: z.record(z.string(), z.object({
            price: z.string().default('0,00'),
            priceScales: z.array(z.object({
                maxAmount: z.number().default(0),
                price: z.string().default('0,00')
            })).default([])
        })),
        colliTypePrices: z.record(z.string(), z.object({
            price: z.string().default('0,00'),
            priceScales: z.array(z.object({
                maxAmount: z.number().default(0),
                price: z.string().default('0,00')
            })).default([])
        })).default({})
    })).default([]),

    // Bezorggebied matrix
    deliveryZones: z.array(z.string()).default([]),
    zoneFees: z.record(z.string(), z.string()).optional(),

    // Fees
    feeExtraStop: z.string(),
    nrOfStopsIncluded: z.number(),
    feeForPickupAddress: z.string(),
    feeForDeliveryAddress: z.string(),
    feeForExtraCollo: z.string(),
    nrOfColliIncluded: z.number(),
    colliTypes: z.array(z.object({
        fee: z.string(),
        type: z.string()
    })).default([]),
    pickupAndDeliveryOptions: z.array(z.object({
        fee: z.string(),
        option: z.string()
    })),

    fees: z.array(OrderFeeModel).default([])
})

// Price tables of shifts, separate from the price tables of orders
const ShiftPriceTableModel = z.object({
    id: z.string(),
    name: z.string(),
    priceBasedOn: z.enum(['workedTime', 'activities']).default('workedTime'),
    pricePerHour: z.string().default('0,00'),
    // Price per hour per activity id
    activities: z.record(z.string(), z.string()).default({}),
    startRate: z.string().default('0,00'),
    minimumRate: z.string().default('0,00'),
    rounding: z.number().or(z.literal('')).optional().nullable()
})

const OrderProductModel = z.object({
    id: z.string(),
    name: z.string(),
    matchAllConditions: z.boolean(),
    conditionGroups: OrderConditionGroupsModel
})

const SupportReasonModel = z.object({
    id: z.string(),
    name: z.string()
})

const OrderCSVExportTemplate = z.object({
    id: z.string(),
    name: z.string(),
    method: z.enum(['order', 'address', 'collo']),
    columns: z.string().array()
})

const RouteCSVExportTemplate = z.object({
    id: z.string(),
    name: z.string(),
    method: z.enum(['route']),
    columns: z.string().array()
})

const InvoiceCSVExportTemplate = z.object({
    id: z.string(),
    name: z.string(),
    method: z.enum(['invoice', 'invoiceLine']),
    columns: z.string().array()
})

const emailNotificationSettings = z.object({
    subject: z.string().default(''),
    body: z.string().default('')
}).default({
    subject: '',
    body: ''
})

const NewResellerModel = z.object({
    name: z.string().default(''),
    isShop: z.boolean().optional(),
    orderPartners: z.string().array().optional(),
    isArchived: z.boolean().optional(),
    archivedTime: z.string().optional(),
    invoiceResellerId: z.string().optional(),
    startDate: z.string().optional().default(() => moment().add(15, 'days').format('YYYY-MM-DD')),
    noInvoice: z.boolean().optional(),
    debtorCode: z.string().optional().default(''),
    reference: z.string().optional().default(''),
    lastInvoice: z.string().optional(),
    accountType: z.enum(['shopPlus', 'carrier', 'carrierPlus']).default('carrier'),
    groups: z.array(z.string()).default([]),
    notes: z.string().nullable().default(''),
    reversedVAT: z.boolean().default(false),
    prices: z.object({
        branchManagement: z.boolean().optional().default(false),
        compensation: z.boolean().optional(),
        minNrOfLicenses: z.number().optional().default(1),
        nrOfFreeLicenses: z.number().optional().default(0),
        licenses: z.array(z.object({
            max: z.number(),
            price: z.string()
        })).default(() => [{max: 99999999, price: '0,00'}]),
        orders: z.array(z.object({
            max: z.number(),
            price: z.string()
        })).default(() => [{max: 99999999, price: '0,00'}]),
        parcels: z.array(z.object({
            max: z.number(),
            price: z.string()
        })).default(() => [{max: 99999999, price: '0,00'}]),
        customPrices: z.boolean().optional().default(false),
        applyLicenseStaffelMax: z.boolean().optional().default(false)
    }).default({}),
    repeatedInvoiceRules: z.array(z.object({
        description: z.string(),
        price: z.number()
    })).default([]),
    permissions: z.object({
        orders: z.boolean().default(true),
        parcels: z.boolean().default(true),
        shifts: z.boolean().default(false),
        paychecks: z.boolean().default(false),
        subscriptions: z.boolean().default(false),
        users: z.boolean().default(true),
        customers: z.boolean().default(true),
        invoices: z.boolean().default(true),
        vehicles: z.boolean().default(false),
        analytics: z.boolean().default(false),
        parcelOCR: z.boolean().default(false),
        network: z.boolean().default(false)
    }).default({}),
    ownerEmail: z.string().optional(),
    msgPostNLenabled: z.boolean().optional(),
    msgDHLenabled: z.boolean().optional(),
    qlsEnabled: z.boolean().optional(),
    intrapostEnabled: z.boolean().optional(),
    snelstartWebshopEnabled: z.boolean().optional(),
    customBrandingEnabled: z.boolean().optional(),
    allowCustomerContracts: z.boolean().optional(),
    shiftTargetsEnabled: z.boolean().optional(),
    // The conditions are stored in the format of server/utils/conditions.ts, older resellers are converted by the migration
    conditionsVersion: z.number().default(CONDITIONS_VERSION),
    postNLMailbox48HEnabled: z.boolean().optional(),
    idlEnabled: z.boolean().optional(),
    settings: z.object({
        account: z.object({
            openingHours: z.object({
                monday: openingHoursDayObject,
                tuesday: openingHoursDayObject,
                wednesday: openingHoursDayObject,
                thursday: openingHoursDayObject,
                friday: openingHoursDayObject,
                saturday: openingHoursDayObject,
                sunday: openingHoursDayObject,
                closed: z.record(z.string(), z.boolean()).default({})
            })
        }).default(() => ({
            openingHours: {
                monday: {open: true, from: '09:00', till: '17:00'},
                tuesday: {open: true, from: '09:00', till: '17:00'},
                wednesday: {open: true, from: '09:00', till: '17:00'},
                thursday: {open: true, from: '09:00', till: '17:00'},
                friday: {open: true, from: '09:00', till: '17:00'},
                saturday: {open: false, from: '09:00', till: '17:00'},
                sunday: {open: false, from: '09:00', till: '17:00'},
                closed: {}
            }
        })),
        accountName: z.string().default(''),
        logo: z.string().optional().default(''),
        logoBlack: z.string().optional().default(''),
        logos: z.object({
            size_32: z.string(),
            size_128: z.string(),
            size_180: z.string(),
            size_192: z.string(),
            size_512: z.string()
        }).partial().optional(),
        apiKey: z.string().optional(),
        brandingEnabled: z.boolean().optional(),
        mainLogo: z.string().optional(),
        squareLogo: z.string().optional(),
        color: z.string().default('#ff9900'),
        domain: z.string().optional().nullable(),
        manualURLReseller: z.string().optional().nullable(),
        manualURLCustomer: z.string().optional().nullable(),
        manualURLMessenger: z.string().optional().nullable(),
        companyAddress: ResellerAddressModel.default(() => ResellerAddressModel.parse({country: 'NL'})),
        sublocations: z.array(AddressModel).default(() => []),
        branches: z.array(z.object({
            id: z.string(),
            name: z.string(),
            address: ResellerAddressModel,
            deliveryZoneIds: z.string().array().default([])
        })).default([]),
        deliveryZones: DeliveryZoneModel.array().default([]),
        useExpectedOrders: z.boolean().default(true),
        pickupNotPossibleReasons: z.string().array().default(() => ['nothome', 'notready', 'invalid', 'addressnotfound', 'wrongaddress', 'companyaddressclosed', 'other']),
        pickupNotPossibleReasonsSpecifications: z.array(z.object({
            reason: z.string(),
            specification: z.string()
        })).default(() => []),
        deliveryNotPossibleReasons: z.string().array().default(() => ['notdelivered;nothome', 'notdelivered;addressnotfound', 'notdelivered;wrongaddress', 'notdelivered;companyaddressclosed', 'notdelivered;verificationfailed', 'missing', 'refused', 'notdelivered;other']),
        deliveryNotPossibleReasonsSpecifications: z.array(z.object({
            reason: z.string(),
            specification: z.string()
        })).default([]),
        defaultSigned: z.boolean().optional(), // deprecated in favor of orders.defaultDeliveryOptions
        nameRequired: z.boolean().optional(), // deprecated in favor of orders.defaultDeliveryOptions
        defaultStatedAddressOnly: z.boolean().optional(), // deprecated in favor of orders.defaultDeliveryOptions
        gpsRequired: z.boolean().optional(), // deprecated in favor of orders.defaultDeliveryOptions
        scanRequired: z.boolean().optional(), // deprecated in favor of orders.defaultDeliveryOptions
        imageRequired: z.boolean().optional(), // deprecated in favor of orders.defaultDeliveryOptions
        allowAddCollo: z.enum(['yes', 'deliver', 'pickup', '']).default('yes'),
        loadingRequiredBeforeStart: z.boolean().optional(),
        deliveryRadiusWarning: z.enum(['', 'warning', 'notallowed']).optional(),
        maxDeliveryRadius: z.number().optional().nullable().default(40),
        nextStopWarning: z.enum(['', 'notallowedanchored', 'notallowed']).optional().default(''),
        nextStopShowWarning: z.boolean().optional(),
        routeNotOptimizedWarning: z.enum(['', 'warning', 'notallowed']).optional().default(''),
        messengerEditCompletedAddress: z.enum(['', 'last', 'route']).optional().default(''),
        messengerEditCompletedAddressMinutes: z.number().min(0).optional().nullable(),
        messengerAddOrderWarning: z.enum(['allowed', 'only_today', 'only_unplanned', 'only_unplanned_today', 'notallowed']).default('notallowed'),
        showWarningOnAddExistingOrder: z.boolean().optional(),
        defaultVehicle: z.enum(['bike', 'car', 'walk']).or(z.string()).optional(),
        customSpeed: z.boolean().optional().default(false),
        speedDifference: z.number().optional().default(0),
        stopTime: z.number().optional().default(180),
        optimizeForTimeslots: z.boolean().optional(),
        adjustableByMessengerGrouped: z.boolean().optional(),
        adjustableByMessenger: z.boolean().optional(),
        allowAddAddress: z.boolean().optional(),
        vehicleTypes: VehicleTypeModel.array().default(() => [
            {
                _id: guid.generate(),
                name: 'Auto',
                icon: 'van-utility',
                routeProfile: 'car' as const,
                fuelType: 'unknown-electric' as const,
                speedDifferenceMode: 'faster' as const,
                usage: 0,
                range: 0,
                payloads: [],
                hourlyRate: '0,00',
                stopTime: 180,
                comment: '',
                notes: '',
                isArchived: false
            },
            {
                _id: guid.generate(),
                name: 'Fiets',
                icon: 'bike',
                routeProfile: 'bike' as const,
                fuelType: 'none' as const,
                speedDifferenceMode: 'faster' as const,
                usage: 0,
                range: 0,
                payloads: [],
                hourlyRate: '0,00',
                stopTime: 180,
                comment: '',
                notes: '',
                isArchived: false
            },
            {
                _id: guid.generate(),
                name: 'Lopen',
                icon: 'walk',
                routeProfile: 'walk' as const,
                fuelType: 'none' as const,
                speedDifferenceMode: 'faster' as const,
                usage: 0,
                range: 0,
                payloads: [],
                hourlyRate: '0,00',
                stopTime: 180,
                comment: '',
                notes: '',
                isArchived: false
            }
        ]),
        useOwnSMTP: z.boolean().optional(),
        smtpUser: z.string().max(100).optional(),
        smtpPassword: z.string().max(100).optional(),
        smtpHost: z.string().max(100).optional(),
        smtpPort: z.string().max(100).optional().nullable(),
        orders: z.object({
            maxLicenses: z.boolean().optional(),
            maxNrOfLicenses: z.number().optional(),
            showNewOrderNotification: z.enum(['never', 'onlyToday', 'always']).default('never'),
            defaultPickupOptions: z.array(OrderPickupOptions.or(z.string())).default([]),
            defaultDeliveryOptions: z.array(OrderDeliveryOptions.or(z.string())).default([]),
            customOptions: z.array(z.object({
                color: z.string(),
                name: z.string(),
                id: z.string()
            })).default([]),
            showOptionsInPlanning: z.enum(['no', 'customOptionsOnly', 'yes']).default('no'),
            allowMessengerEditCustomOptions: z.boolean().default(false),
            colliTypes: z.array(z.object({
                name: z.string(),
                id: z.string()
            })).default(Object.entries(dbCodes.colloTypes).map(([id, name]) => ({id, name}))),
            defaultColliTypes: z.array(z.string()).default(Object.keys(dbCodes.colloTypes)),
            emailNotifications: z.object({
                senderName: z.string().default(''),
                replyToEmail: z.string().default(''),
                defaultNotificationsReceiver: z.array(z.string()).default(() => []),
                defaultNotificationsCustomer: z.array(z.string()).default(() => []),
                pickup_registered: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                pickup_eta: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                pickup_ontheway: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                pickup_delayed: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                pickup_nothome: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                pickup_completed: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_registered: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_collected: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_eta: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_ontheway: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_delayed: z.object({
                    reschedule: emailNotificationSettings,
                    retour: emailNotificationSettings,
                    nofollowup: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_nothome: z.object({
                    reschedule: emailNotificationSettings,
                    onhold: emailNotificationSettings,
                    retour: emailNotificationSettings,
                    nofollowup: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                delivery_completed: z.object({
                    delivered: emailNotificationSettings,
                    deliveredAtNeighbors: emailNotificationSettings,
                    deliveredInMailbox: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                customer_pickup_completed: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({}),
                customer_delivery_completed: z.object({
                    default: emailNotificationSettings,
                    label: z.string().default(''),
                    comment: z.string().default('')
                }).default({})
            }).default(() => defaultEmailNotifications()),
            trackTrace: z.object({
                showFaq: z.boolean().optional(),
                faq: z.array(z.object({
                    question: z.string(),
                    answer: z.string()
                })).default([]),
                footerTitle: z.string().default(''),
                footerMessage: z.string().default(''),
                pickupNotCompletedMessage: z.string().default(''),
                pickupNotHomeMessage: z.string().default(''),
                pickupCancelledMessage: z.string().default(''),
                deliveryNotCompletedRescheduleMessage: z.string().default(''),
                deliveryNotCompletedRetourMessage: z.string().default(''),
                deliveryNotCompletedRefusedMessage: z.string().default(''),
                deliveryNotCompletedNoFollowUpMessage: z.string().default(''),
                deliveryNotHomeRescheduleMessage: z.string().default(''),
                deliveryNotHomeOnHoldMessage: z.string().default(''),
                deliveryNotHomeRetourMessage: z.string().default(''),
                deliveryNotHomeNoFollowUpMessage: z.string().default(''),
                deliveryCancelledMessage: z.string().default(''),
                deliveryMancoMessage: z.string().default(''),
                timeFrame: z.number().default(60),
                hideAddress: z.boolean().optional(),
                email: z.string().default(''),
                phone: z.string().default(''),
                outForDeliveryStatusIcon: z.enum(['bike', 'cargo-bike', 'car']).default('bike'),
                hidePhoto: z.boolean().optional()
            }).default(() => ({
                showFaq: false,
                faq: [],
                footerTitle: 'Vragen?',
                footerMessage: 'Kijk bij de veelgestelde vragen of neem contact met ons op.',
                pickupNotCompletedMessage: 'Het is ons helaas niet gelukt om je zending op te halen, onze excuses hiervoor. Neem contact op met de afzender om een nieuwe afspraak te maken.',
                pickupNotHomeMessage: 'Je was niet thuis toen we je zending kwamen ophalen. Neem contact op met de afzender om een nieuwe afspraak te maken.',
                pickupCancelledMessage: 'Je ophaalafspraak is geannuleerd. Neem contact op met de afzender voor meer informatie.',
                deliveryNotCompletedRescheduleMessage: 'Het is ons helaas niet gelukt je zending te bezorgen, onze excuses hiervoor. Hieronder vind je de nieuwe bezorgdatum.',
                deliveryNotCompletedRetourMessage: 'Je zending gaat retour naar de afzender omdat het adres niet correct is. Neem contact op met de afzender voor meer informatie.',
                deliveryNotCompletedRefusedMessage: 'Je zending gaat retour naar de afzender omdat de bezorging geweigerd is. Neem contact op met de afzender voor meer informatie.',
                deliveryNotCompletedNoFollowUpMessage: 'Het is ons helaas niet gelukt je zending te bezorgen, onze excuses hiervoor. Er staat voorlopig geen nieuwe bezorgpoging gepland. Neem contact op met de afzender voor meer informatie.',
                deliveryNotHomeRescheduleMessage: 'Je was niet thuis om je zending in ontvangst te nemen. Hieronder vind je de nieuwe bezorgdatum.',
                deliveryNotHomeOnHoldMessage: 'Je was niet thuis om je zending in ontvangst te nemen. Neem contact met ons op om een nieuwe bezorgafspraak te maken.',
                deliveryNotHomeRetourMessage: 'Je was niet thuis om je zending in ontvangst te nemen. De zending gaat retour naar de afzender.',
                deliveryNotHomeNoFollowUpMessage: 'Je was niet thuis om je zending in ontvangst te nemen. Er staat voorlopig geen nieuwe bezorgpoging gepland. Neem contact op met de afzender voor meer informatie.',
                deliveryCancelledMessage: 'Je bezorging is geannuleerd. Neem contact op met de afzender voor meer informatie.',
                deliveryMancoMessage: 'Je bezorging is manco. Neem contact op met de afzender voor meer informatie.',
                timeFrame: 60,
                email: '',
                phone: '',
                outForDeliveryStatusIcon: 'bike' as const
            })),
            prices: z.array(OrderPriceTableModel).default([]),
            products: z.array(OrderProductModel).default([]),
            autoCompleteRoutes: z.boolean().optional(),
            exportCsvTemplates: OrderCSVExportTemplate.array().default([]),
            csvColumns: z.record(z.string(), z.string().array()).default({}),
            csvColumnsPerOrderType: z.record(z.string(), z.object({
                delivery: z.string().array().optional(),
                pickup: z.string().array().optional(),
                pickupDelivery: z.string().array().optional(),
                multiOrder: z.string().array().optional()
            })).default({}),
            defaultDate: z.enum(['today', 'selectedDate']).default('today').optional(),
            defaultAddresses: z.enum(['deliver', 'pickup', 'pickupAndDeliver']).default('deliver').optional(),
            autoMailboxOption: z.boolean().optional(),
            monitorTemperature: z.boolean().optional(),
            sensors: z.object({
                id: z.string(),
                name: z.string(),
                sensorId: z.string(),
                minTemperature: z.number().optional(),
                maxTemperature: z.number().optional()
            }).array().default([])
        }).default({}),
        parcels: z.object({
            carriers: z.intersection(
                z.object({
                    partners: z.string().array(),
                    isVeloydPartner: z.boolean().optional()
                }),
                z.record(z.string(), z.any()) // TODO: type carriers settings
            ).default(() => ({
                'partners': [],
                'DHL': {
                    enabled: false,
                    accountId: '',
                    apiKey: ''
                },
                'PostNL': {
                    enabled: false,
                    customerNumber: '',
                    customerCode: '',
                    customerCodeInt: '',
                    apiKey: ''
                },
                'Skynet': {
                    enabled: false,
                    username: '',
                    password: '',
                    apiKey: ''
                },
                'GLS': {
                    enabled: false
                },
                'PostNL-MSG': {
                    enabled: false,
                    username: '',
                    password: '',
                    originName: ''
                }
            })),
            trackTrace: z.object({
                senderName: z.string(),
                replyToEmail: z.string(),
                emailSubject: z.string(),
                emailBody: z.string(),
                sendTTMails: z.boolean()
            }).default(() => ({
                sendTTMails: false,
                senderName: '',
                replyToEmail: '',
                emailSubject: 'Track & Trace',
                emailBody: defaultTrackTraceBody
            })),
            cancelEmail: z.object({
                sendCancelEmails: z.boolean(),
                senderName: z.string(),
                replyToEmail: z.string(),
                emailSubject: z.string(),
                emailBody: z.string()
            }).default(() => ({
                sendCancelEmails: false,
                senderName: '',
                replyToEmail: '',
                emailSubject: 'Zending {barcode} geannuleerd',
                emailBody: defaultCancelEmailBody
            })),
            prices: z.record(z.string(), ParcelPriceTableModel).default(() => ({})), // PriceTables names
            fees: z.array(ParcelFeeModel).default([]),
            products: z.record(
                z.string(), z.record(
                    z.string(), z.boolean() // {productName: {carrierName: boolean}}
                )).default(() => ({})),
            productCodes: z.record(z.string(), z.record(z.string(), z.array(z.object({
                name: z.string(),
                countries: z.array(z.string()),
                type: z.string().optional(),
                productCode: z.string().optional(),
                kgProductCode: z.string().optional(),
                categories: z.array(z.object({
                    name: z.string().optional(),
                    minWeight: z.string().optional(),
                    maxWeight: z.string(),
                    length: z.number().optional(),
                    width: z.number().optional(),
                    height: z.number().optional(),
                    volume: z.number().optional(),
                    productCode: z.string()
                }))
            })))).default({}),
            // Besides name and countries, a preference holds the ranking per carrier by carrier name (e.g. DHL: '1')
            preferences: z.record(z.string(), z.array(
                z.object({
                    name: z.string(),
                    countries: z.array(z.string())
                }).catchall(z.string()).or(z.record(z.string(), z.string()))
            )).default({}),
            shippingRules: z.array(ShippingRuleSetModel).default([]),
            customerSupport: z.object({
                enabled: z.boolean().default(false),
                email: z.string().default(''),
                modalTitle: z.string().default(''),
                modalText: z.string().default(''),
                supportReasons: z.array(SupportReasonModel).default(() => [{id: generateUUID(), name: 'Overig'}])
            }).default({}),
            csvColumns: z.record(z.string(), z.string().array()).default({}),
            csvOptions: z.record(z.string(), z.object({
                optionsFromCSV: z.boolean(),
                businessAddress: z.boolean(),
                ignoreFirstLine: z.boolean()
            })).default({})
        }).default({}),
        routes: z.object({
            exportCsvTemplates: RouteCSVExportTemplate.array().default([])
        }).default({}),
        customers: z.object({
            senderName: z.string().default(''),
            replyToEmail: z.string().default(''),
            welcomeEmailSubject: z.string().default(''),
            welcomeEmailBody: z.string().default('')
        }).default(() => ({
            welcomeEmailSubject: 'Account voor Veloyd',
            welcomeEmailBody: defaultWelcomeCustomerBody
        })),
        users: z.object({
            welcomeEmailSubject: z.string().default(''),
            welcomeEmailBody: z.string().default('')
        }).default(() => ({
            welcomeEmailSubject: 'Account voor Veloyd',
            welcomeEmailBody: defaultWelcomeUserBody
        })),
        shifts: z.object({
            activities: z.array(ActivityModel).default([]),
            prices: z.array(ShiftPriceTableModel).default([]),
            shiftRequired: z.boolean().default(false),
            gpsRequired: z.boolean().default(false),
            deviateStartLocation: z.enum(['', 'warning', 'notallowed']).optional().default(''),
            deviateEndLocation: z.enum(['', 'warning', 'notallowed']).optional().default(''),
            maxStartEndLocationDeviation: z.number().optional().default(100),
            startShiftBeforeStartTime: z.enum(['allowed', 'allowedmargin', 'notallowed']).default('allowed'),
            startShiftBeforeStartTimeMargin: z.number().default(0),
            lateStart: z.object({
                warning: z.enum(['', 'os', 'email', 'both']).optional().default(''),
                notificationMinutes: z.number().optional().nullable().default(null),
                recipients: z.array(z.string()).optional().default([]),
                subject: z.string().optional().default(''),
                senderEmail: z.string().optional().default('')
            }).default({}),
            shiftbase: z.object({
                enabled: z.boolean().default(false),
                autoSyncShifts: z.boolean().default(false),
                apiKey: z.string().default(''),
                minimalShiftDuration: z.number().default(0),
                departmentConnectedTo: z.enum(['user', 'shift', 'customer']).default('shift'),
                teamConnectedTo: z.enum(['user', 'shift', 'customer']).default('user'),
                importShifts: z.enum(['match', 'linkedUser', 'all']).default('match')
            }).default({
                enabled: false,
                autoSyncShifts: false,
                apiKey: '',
                minimalShiftDuration: 0,
                departmentConnectedTo: 'shift',
                teamConnectedTo: 'user',
                importShifts: 'match'
            })
        }).default({}),
        administration: z.object({
            ordersProductCode: z.string().default(''),
            parcelsProductCode: z.string().default(''),
            shiftsProductCode: z.string().default(''),
            subscriptionsProductCode: z.string().default(''),
            setPeriod: z.boolean().default(false),
            periodAsReference: z.boolean().default(false),
            api: z.string().default(''),
            apiKey: z.string().default(''),
            customerGroup: z.string().default(''),
            checkConceptInvoices: z.boolean().default(false),
            mergeOrders: z.boolean().default(false),
            mergeParcels: z.enum(['', 'carrier', 'productCode']).default(''),
            apiName: z.string().default(''),
            clientId: z.string().default(''),
            clientSecret: z.string().default(''),
            apiInvoiceVATCode: z.string().default(''),
            apiInvoiceDim: z.string().default(''),
            apiPassword: z.string().default(''),
            apiOrganisation: z.string().default(''),
            apiOffice: z.string().default(''),
            apiInvoiceType: z.string().default(''),
            invoicePriceZero: z.boolean().default(false),
            invoiceParcelsPriceZero: z.boolean().default(false),
            invoiceSendOrders: z.boolean().default(false),
            invoiceSendParcels: z.boolean().default(false),
            checkOrders: z.boolean().default(false),
            showNameOnInvoice: z.boolean().default(true),
            showPriceExplanationOrders: z.boolean().default(false),
            showPriceExplanationParcels: z.boolean().default(false),
            showColliBarcodeSpecification: z.boolean().default(false),
            showOrderOverviewOnInvoice: z.boolean().default(false),
            groupOrderOverviewByDay: z.boolean().default(false),
            showOrderSpecificationOnInvoice: z.boolean().default(true),
            showParcelOverviewOnInvoice: z.boolean().default(false),
            showParcelSpecificationOnInvoice: z.boolean().default(true),
            xmlExportTemplate: z.string().default(''),
            xmlExport: z.boolean().default(false),
            sendAttachment: z.boolean().default(false),
            apiEnvironment: z.string().default(''),
            syncCustomers: z.enum(['', 'yes', 'fromVeloyd', 'toVeloyd']).default(''),
            noNewCustomers: z.boolean().default(false),
            customersNotEditable: z.boolean().default(false),
            snelstartSubscriptionKey: z.string().default(''),
            snelstartClientKey: z.string().default(''),
            afasCustomerGetConnector: z.string().optional(),
            afasGUIDDebtorName: z.string().optional(),
            afasGUIDDebtorId: z.string().optional(),
            afasGUIDEmail: z.string().optional(),
            afasGUIDPhone: z.string().optional(),
            afasGUIDStreet: z.string().optional(),
            afasGUIDNr: z.string().optional(),
            afasGUIDAddition: z.string().optional(),
            afasGUIDStreet2: z.string().optional(),
            afasGUIDPostalCode: z.string().optional(),
            afasGUIDCity: z.string().optional(),
            afasGUIDCountryCode: z.string().optional(),
            afasGUIDCountry: z.string().optional(),
            afasAdministration: z.string().optional(),
            afasTypeItem: z.string().optional(),
            afasUnit: z.string().optional(),
            afasWarehouse: z.string().optional(),
            afasGUIDVeloydId: z.string().optional(),
            afasGUIDCustomerReference: z.string().optional(),
            afasGUIDTrackTrace: z.string().optional(),
            afasGUIDCarrier: z.string().optional(),
            afasGUIDOptions: z.string().optional(),
            afasGUIDWeight: z.string().optional(),
            afasGUIDInfo: z.string().optional(),
            exportCsvTemplates: InvoiceCSVExportTemplate.array().default([])
        }).default({}),
        partnerKey: z.string().default(() => generateUUID()),
        defaultPartnerSettings: z.object({
            // Orders the reseller forwards to partners
            outgoing: z.object({
                useCustomScanColor: z.boolean().optional(),
                customScanColor: z.string().default('#ff9900'),
                creditorCode: z.string().optional(),
                communication: z.string().default('partner'), // 'reseller' or 'partner', see partnerCommunication in orders.ts
                allowEditOrder: z.enum(['yes', 'addressOnly', 'no']).default('yes'),
                autoForwardOrders: z.boolean().optional().default(false),
                conditionGroups: OutgoingPartnerConditionGroupsModel,
                conditionsMatchAll: z.boolean().default(true)
            }).default({}),
            // Orders the reseller receives from partners
            incoming: z.object({
                showRouteInfo: z.boolean().optional(),
                deliveryZones: z.string().array().default([]),
                postalCodes: z.string().optional(),
                maxWeight: z.number().optional(),
                maxVolume: z.number().optional(),
                maxLength: z.number().optional(),
                maxWidth: z.number().optional(),
                maxHeight: z.number().optional(),
                conditionGroups: IncomingPartnerConditionGroupsModel,
                conditionsMatchAll: z.boolean().default(true)
            }).default({})
        }).default({}),
        // Partners the reseller forwards orders to, mirrored by an entry in the partner's incomingPartners
        outgoingPartners: OutgoingPartnerModel.array().default([]),
        // Partners the reseller receives orders from, mirrored by an entry in the partner's outgoingPartners
        incomingPartners: IncomingPartnerModel.array().default([]),
        // Forwarding of orders to IDL (Shipix Transport Orders API), only available when idlEnabled is set by an admin
        idl: z.object({
            active: z.boolean().default(false),
            apiKey: z.string().default(''),
            // Used as contactPhone for stops without a phone number (required by Shipix)
            defaultContactPhone: z.string().default(''),
            // Packaging type of the items, the keys are set up per relation by IDL
            packagingTypeKey: z.string().default('BIGBAG'),
            forwardOrders: z.enum(['all', 'conditions']).default('all'),
            conditionGroups: OrderConditionGroupsModel.default([]),
            matchAllConditions: z.boolean().default(true)
        }).default({}),
        mfaWebEnabled: z.boolean().optional(),
        mfaAppEnabled: z.boolean().optional()
    }).default({}).transform((settings) => {
        if (settings.defaultVehicle === undefined) {
            settings.defaultVehicle = settings.vehicleTypes.find((vehicleType) => vehicleType.routeProfile === 'bike')?._id ?? settings.vehicleTypes[0]?._id
        }

        return settings
    })
})

const nullToUndefined = (value: unknown): unknown => {
    if (value === null) return undefined

    if (Array.isArray(value)) {
        return value.map(nullToUndefined)
    }

    // Only plain objects: rebuilding a Date (e.g. a carrier token expireDate) would turn it into {}
    if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
        return Object.fromEntries(
            Object.entries(value).map(([k, v]) => [k, nullToUndefined(v)])
        )
    }

    return value
}

const ResellerModel = z.preprocess(
    nullToUndefined, NewResellerModel.extend({
        _id: z.string()
    }))

export {
    NewResellerModel,
    ResellerModel,
    ParcelPriceTableModel,
    ParcelFeeModel,
    VehicleTypeModel,
    ShippingRuleModel,
    RouteProfileModel,
    FuelTypeModel,
    PayloadTypeModel,
    DeliveryZoneModel,
    OutgoingPartnerModel,
    IncomingPartnerModel,
    OutgoingPartnerConditionGroupsModel,
    IncomingPartnerConditionGroupsModel,
    SupportReasonModel,
    ActivityModel,
    OrderProductModel,
    OrderPriceTableModel,
    ShiftPriceTableModel
}
